import { useMemo } from "react";

import type { ConnectedProjectDto, GenericAlertTriggeredDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Mail01Icon } from "@/components/Icons/Mail01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Pagination } from "@/components/Pagination/Pagination";
import { Button } from "@/design-system/Button";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface AlertsListProps {
  triggeredAlerts: GenericAlertTriggeredDto[];
  isLoading: boolean;
  pageIndex: number;
  totalPages: number;
  projects: ConnectedProjectDto[];
  onPageChange: (page: number) => void;
  projectId?: string;
  previousProjectId?: string;
  onProjectChange: (tab: string | undefined) => void;
}
const MAX_PROJECT_TABS = 8;
export function AlertsList({
  triggeredAlerts,
  isLoading,
  pageIndex,
  totalPages,
  projects,
  onPageChange,
  projectId,
  previousProjectId,
  onProjectChange,
}: AlertsListProps): React.ReactNode {
  const connectedProjects = useLinkedProjects();
  const { t } = useTranslation();

  const triggeredAlertsColumns = useMemo(() => {
    const helper = createDashboardColumnHelper<GenericAlertTriggeredDto>();

    return [
      helper.accessor("messageId", {
        header: t("page.alerts-overview.table.link"),
        cell: (cell) => cell.row.original.content,
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.row.original.content,
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("alertType", {
        header: t("page.alerts-overview.table.alert-type"),
        cell: (cell) => {
          function getTranslation() {
            switch (cell.getValue()) {
              case "commentKeyword":
                return t("page.alerts-overview.table.alert-type.comment-keyword");
              case "postKeyword":
                return t("page.alerts-overview.table.alert-type.post-keyword");
              case "likeThreshold":
                return t("page.alerts-overview.table.alert-type.likes-threshold");
              case "commentThreshold":
                return t("page.alerts-overview.table.alert-type.comments-threshold");
              default:
                return "-";
            }
          }

          return (
            <span>
              <span className="whitespace-nowrap">{getTranslation()}</span>
              {cell.row.original.matchedKeywords ? (
                <span className="ml-space-xs text-caption wrap-break-word text-text-secondary">
                  ({cell.row.original.matchedKeywords})
                </span>
              ) : null}
            </span>
          );
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("alertName", {
        header: t("page.alerts-overview.table.alert-name"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("projectName", {
        header: t("page.alerts-overview.table.project-name"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("sentAt", {
        header: t("page.alerts-overview.table.sent-at"),
        cell: (cell) => (
          <span className="flex items-center gap-space-xs truncate">
            <FormattedDate date={cell.getValue()} format="datetimeRelative" />
            {cell.row.original.emailSent ? (
              <span role="img" aria-label={t("page.alerts-overview.table.email-sent")} className="text-text-secondary">
                <Mail01Icon />
              </span>
            ) : null}
          </span>
        ),
        minSize: TABLE_COLUMN_SIZES.DATE_TIME.MIN,
        size: TABLE_COLUMN_SIZES.DATE_TIME.DEFAULT,
      }),
    ];
  }, [t]);
  const commentKeywordsTableInstance = useResizableTable(TABLE_KEYS.ALERTS, {
    columns: triggeredAlertsColumns,
    data: triggeredAlerts,
    enableSorting: false,
  });

  const tabItems: TabItem<string>[] = useMemo(() => {
    const projectsWithPermission = projects.filter((x) => x.userRole !== "resident" && x.userRole !== "maintenance");

    return [
      {
        value: "all",
        label: "All",
      },
      ...(projectsWithPermission.length > MAX_PROJECT_TABS
        ? projectsWithPermission
            .filter((x) => x.id === projectId || x.id === previousProjectId)
            .map((x) => ({
              value: x.id,
              label: x.name,
            }))
        : projectsWithPermission.map((x) => ({
            value: x.id,
            label: x.name,
          }))),
    ] satisfies TabItem<string>[];
  }, [projects, projectId, previousProjectId]);

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.alerts-overview.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.alerts-overview.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{}}
            context={{ scope: "portfolio", entity: "alerts", page: "list" }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.alerts.suggestion.1"),
              t("component.ask-nobi-chat-drawer.alerts.suggestion.2"),
            ]}
          />
          <Button type="link" href={routes.alerts.settings()}>
            {t("page.alerts-overview.settings")}
          </Button>
        </>
      }
    >
      <Tabs.Root
        activeTab={projectId || "all"}
        onSelectTab={(x) => onProjectChange(x === "all" ? undefined : x)}
        tabs={tabItems}
      >
        <Tabs.Content value={projectId || "all"}>
          {triggeredAlerts.length > 0 || isLoading ? (
            <Table
              table={commentKeywordsTableInstance}
              isLoading={isLoading}
              getRowHref={(row) => {
                const projectId = row.original.projectId;
                const slug = connectedProjects.data?.find((x) => x.id === projectId)?.slug;

                if (!slug) {
                  return "";
                }

                return routes.communityFeed.postDetails({
                  slug: slug,
                  id: row.original.messageId,
                });
              }}
            >
              <Pagination count={totalPages} currentIndex={pageIndex} onChange={onPageChange} />
            </Table>
          ) : (
            <section className="px-space-md py-space-lg">
              <span className="text-caption">{t("page.alerts-overview.no-data")}</span>
            </section>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </DocumentPage>
  );
}
